import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { ListChecks, Plus, Trash2, GripVertical, Save, RotateCcw, Camera } from "lucide-react";

/**
 * Manager-only settings page for the workspace checklist library.
 *
 * The library is the master list of items managers can tick on when
 * creating/editing a job. Jobs still store copies of the strings they
 * use, so editing / removing items here never affects previously-created
 * jobs — it only changes what shows up as a quick-pick next time.
 */
export default function ChecklistLibraryPage() {
    const [items, setItems] = useState([]);
    // Mirror of items that require a photo before they can be ticked.
    const [photoRequired, setPhotoRequired] = useState([]);
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverItems, setServerItems] = useState([]);
    const [serverPhotoRequired, setServerPhotoRequired] = useState([]);

    const dirty = JSON.stringify(items) !== JSON.stringify(serverItems)
        || JSON.stringify([...photoRequired].sort()) !== JSON.stringify([...serverPhotoRequired].sort());

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/checklist-library");
            const list = Array.isArray(data?.items) ? data.items : [];
            const pr = Array.isArray(data?.photo_required_items) ? data.photo_required_items : [];
            setItems(list); setServerItems(list);
            setPhotoRequired(pr); setServerPhotoRequired(pr);
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't load the library");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const addDraft = () => {
        const v = draft.trim();
        if (!v) return;
        if (items.some(x => x.toLowerCase() === v.toLowerCase())) {
            toast.error("That item is already in the list");
            return;
        }
        setItems([...items, v]);
        setDraft("");
    };

    const updateItem = (idx, value) => {
        const prev = items[idx];
        const next = [...items];
        next[idx] = value;
        setItems(next);
        // Keep the photo-required list in sync when an item is renamed.
        if (prev !== value && photoRequired.includes(prev)) {
            setPhotoRequired(photoRequired.map(x => x === prev ? value : x));
        }
    };

    const removeItem = (idx) => {
        const victim = items[idx];
        setItems(items.filter((_, i) => i !== idx));
        setPhotoRequired(photoRequired.filter(x => x !== victim));
    };

    const togglePhotoRequired = (item) => {
        setPhotoRequired(photoRequired.includes(item)
            ? photoRequired.filter(x => x !== item)
            : [...photoRequired, item]);
    };

    const move = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= items.length) return;
        const next = [...items];
        [next[idx], next[target]] = [next[target], next[idx]];
        setItems(next);
    };

    const save = async () => {
        const clean = items.map(s => (s || "").trim()).filter(Boolean);
        const cleanSet = new Set(clean);
        const cleanPr = photoRequired.filter(x => cleanSet.has(x));
        setSaving(true);
        try {
            const { data } = await api.put("/checklist-library", {
                items: clean,
                photo_required_items: cleanPr,
            });
            const list = Array.isArray(data?.items) ? data.items : [];
            const pr = Array.isArray(data?.photo_required_items) ? data.photo_required_items : [];
            setItems(list); setServerItems(list);
            setPhotoRequired(pr); setServerPhotoRequired(pr);
            toast.success("Library saved");
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't save the library");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="checklist-library-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
                        <ListChecks size={28} /> Checklist library
                    </h1>
                    <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-xl">
                        The master list operatives can be asked to tick off before clocking out of a job.
                        Each job still picks its own subset — editing an item here only changes the quick-pick
                        list for new jobs.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        data-testid="library-reset-btn"
                        onClick={() => { setItems(serverItems); setPhotoRequired(serverPhotoRequired); }}
                        disabled={!dirty || saving}
                        className="btn-ghost inline-flex items-center gap-2 text-sm disabled:opacity-50">
                        <RotateCcw size={14} /> Reset
                    </button>
                    <button
                        data-testid="library-save-btn"
                        onClick={save}
                        disabled={!dirty || saving}
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                        <Save size={14} /> {saving ? "Saving…" : "Save library"}
                    </button>
                </div>
            </div>

            <section className="card-flat p-6">
                {loading ? (
                    <div className="text-sm text-[var(--text-muted)]">Loading…</div>
                ) : (
                    <>
                        <div className="space-y-2" data-testid="library-items-list">
                            {items.length === 0 && (
                                <div className="text-sm text-[var(--text-muted)]">
                                    No items yet. Add your first one below.
                                </div>
                            )}
                            {items.map((item, i) => {
                                const needsPhoto = photoRequired.includes(item);
                                return (
                                    <div key={i} className="flex items-center gap-2 p-2 rounded-xl border border-[var(--border-soft)] bg-white">
                                        <div className="flex flex-col text-[var(--text-muted)]">
                                            <button
                                                type="button"
                                                onClick={() => move(i, -1)}
                                                disabled={i === 0}
                                                className="disabled:opacity-30 hover:text-[var(--pine)] leading-none"
                                                aria-label="Move up">▲</button>
                                            <button
                                                type="button"
                                                onClick={() => move(i, 1)}
                                                disabled={i === items.length - 1}
                                                className="disabled:opacity-30 hover:text-[var(--pine)] leading-none"
                                                aria-label="Move down">▼</button>
                                        </div>
                                        <GripVertical size={14} className="text-[var(--text-muted)] opacity-40" />
                                        <input
                                            data-testid={`library-item-input-${i}`}
                                            className="input-field flex-1"
                                            value={item}
                                            onChange={(e) => updateItem(i, e.target.value)}
                                        />
                                        <label
                                            data-testid={`library-item-photo-required-${i}`}
                                            className={`inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${
                                                needsPhoto
                                                    ? "border-[var(--terracotta)] bg-[#FFF7E6] text-[var(--terracotta)] font-semibold"
                                                    : "border-[var(--border-soft)] text-[var(--text-muted)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]"
                                            }`}
                                            title="Operative must attach a photo before this item can be ticked">
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={needsPhoto}
                                                onChange={() => togglePhotoRequired(item)} />
                                            <Camera size={12} /> Photo
                                        </label>
                                        <button
                                            type="button"
                                            data-testid={`library-item-remove-${i}`}
                                            onClick={() => removeItem(i)}
                                            className="btn-ghost !p-2 text-red-600"
                                            aria-label="Remove">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-4 flex gap-2">
                            <input
                                data-testid="library-draft-input"
                                className="input-field"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDraft(); } }}
                                placeholder="Add a new item (e.g. &quot;Wipe skirting boards&quot;)" />
                            <button
                                type="button"
                                data-testid="library-draft-add-btn"
                                onClick={addDraft}
                                className="btn-accent inline-flex items-center gap-2 whitespace-nowrap">
                                <Plus size={14} /> Add
                            </button>
                        </div>

                        {dirty && (
                            <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="library-dirty-banner">
                                You have unsaved changes.
                            </div>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}
